import React from 'react';
import { Link } from 'react-router-dom';
import ReportNote from '../components/ReportNote';

const SECTIONS = [
  {
    title: 'Time & Workload',
    reports: [
      {
        to: '/reports/team-kpi', label: 'Team KPI',
        what: 'Daily time-based KPIs: does each person have enough logged work?',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>,
      },
      {
        to: '/reports/monthly-time', label: 'Logged Time vs Target',
        what: 'Time logged per person against the 7.8h per working day target.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01"/></svg>,
      },
      {
        to: '/reports/capacity', label: 'Capacity',
        what: 'Team availability against planned and logged work.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
      },
      {
        to: '/reports/billable', label: 'Billable Time',
        what: 'Billable versus non-billable logged time per member.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
      },
      {
        to: '/reports/weekly', label: 'Weekly Report',
        what: 'Tickets handled and time logged by each team member.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
      },
      {
        to: '/reports/performance', label: 'Team Performance',
        what: 'Per-person ticket throughput and resolution times.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
      },
    ],
  },
  {
    title: 'Customers & Delivery',
    reports: [
      {
        to: '/reports/customers', label: 'Customer Activity',
        what: 'Billable tickets and logged time per customer project.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
      },
      {
        to: '/reports/client', label: 'Rapport Client',
        what: 'Activity and logged time for the selected client.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
      },
      {
        to: '/reports/incidents', label: 'Incidents',
        what: 'Incidents created and resolved, broken down by priority.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
      },
      {
        to: '/reports/sla', label: 'SLA',
        what: 'SLA performance: response and resolution against target.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
      },
      {
        to: '/reports/recurrence', label: 'Récurrence',
        what: 'Tickets that recur for the same customer or subject.',
        icon: <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>,
      },
    ],
  },
];

export default function ReportsPage() {
  return (
    <div>
      <ReportNote what="All reports, in one place." />
      {SECTIONS.map(section => (
        <div key={section.title} style={{ marginBottom: 28 }}>
          <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-3)', marginBottom: 12 }}>
            {section.title}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
            {section.reports.map(r => (
              <Link key={r.to} to={r.to} className="reports-hub-card card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', textDecoration: 'none', color: 'inherit' }}>
                <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, background: 'var(--accent-light)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {r.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.4 }}>{r.what}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}

      <style>{`
        .reports-hub-card { transition: border-color .15s, box-shadow .15s, transform .15s; }
        .reports-hub-card:hover { border-color: var(--accent-border); box-shadow: var(--shadow-md); transform: translateY(-1px); }
      `}</style>
    </div>
  );
}
